import React, { useEffect, useState, useCallback } from 'react'
import type {
  CognitionEvaluationChatMessage,
  CognitionEvaluationRole,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import AiChat, { AiChatProps, AiChatMode } from './'
import type { AutomationTarget } from '../../types'

export interface AiChatContainerReadOnlyProps
  extends AutomationTarget,
    Pick<
      AiChatProps,
      | 'className'
      | 'context'
      | 'externalPrompt'
      | 'hideChatFeedback'
      | 'introMessage'
      | 'onCopy'
      | 'onExternalPromptProcessed'
      | 'onFeedback'
      | 'showContext'
      | 'showContextAsLink'
      | 'open'
    > {
  mode: 'mock' | 'trace'
  externalMockResponse?: string
  messages?: CognitionEvaluationChatMessage[]
}

const AiChatContainerReadOnly: React.FC<AiChatContainerReadOnlyProps> = ({
  mode,
  externalPrompt,
  externalMockResponse,
  messages: initialMessages,
  className,
  context,
  onCopy,
  onExternalPromptProcessed,
  showContext,
  showContextAsLink,
  hideChatFeedback,
  introMessage,
  open,
  elmId,
}) => {
  const [messages, setMessages] = useState<CognitionEvaluationChatMessage[]>([])
  const [isAwaitingReply, setIsAwaitingReply] = useState(false)

  // Convert externalPrompt and externalMockResponse to messages format
  useEffect(() => {
    if (mode === 'mock' && externalPrompt && externalMockResponse) {
      const mockMessages: CognitionEvaluationChatMessage[] = [
        {
          object: {
            message: externalPrompt,
          },
          role: graph.enums.CognitionEvaluationRole
            .User as CognitionEvaluationRole,
          sequence: 1,
          type: 'object',
          __typename: 'CognitionEvaluationChatInputMessage',
        },
        {
          object: {
            message: externalMockResponse,
          },
          role: graph.enums.CognitionEvaluationRole
            .Model as CognitionEvaluationRole,
          sequence: 2,
          type: 'object',
          __typename: 'CognitionEvaluationChatOutputMessage',
        },
      ]

      // For mock mode, show messages with delay
      setMessages([])
      setIsAwaitingReply(false)

      // Show user message immediately
      setTimeout(() => {
        setMessages([mockMessages[0]])
        setIsAwaitingReply(true)
      }, 100)

      // Show AI response after delay
      setTimeout(() => {
        setMessages(mockMessages)
        setIsAwaitingReply(false)
        if (onExternalPromptProcessed) {
          onExternalPromptProcessed()
        }
      }, 2000) // 2 second delay for AI response
    } else if (initialMessages && initialMessages.length > 0) {
      // For trace mode or when messages are provided directly
      setMessages(initialMessages)
      setIsAwaitingReply(false)
    }
  }, [
    mode,
    externalPrompt,
    externalMockResponse,
    initialMessages,
    onExternalPromptProcessed,
  ])

  // No-op for upsertEvaluationGroup since we're read-only
  const upsertEvaluationGroup = useCallback(async () => {
    // Do nothing - read-only mode
  }, [])

  // Wrapper for onCopy to allow consumer callback chaining
  const handleCopy = (content: string) => {
    if (onCopy) {
      onCopy(content)
    }
  }

  return (
    <div
      data-elm-id={elmId ? `${elmId}_container` : undefined}
      style={{ height: '100%' }}
    >
      <AiChat
        mode={mode as AiChatMode}
        upsertEvaluationGroup={upsertEvaluationGroup}
        messages={messages}
        isAwaitingReply={isAwaitingReply}
        className={className}
        context={context}
        onCopy={handleCopy}
        externalPrompt={externalPrompt}
        onExternalPromptProcessed={onExternalPromptProcessed}
        showContext={showContext}
        showContextAsLink={showContextAsLink}
        hideChatFeedback={hideChatFeedback}
        introMessage={introMessage}
        open={open}
        elmId={elmId}
      />
    </div>
  )
}

export default AiChatContainerReadOnly
